import React, { useState, useRef, useEffect } from 'react';
import { Search, MapPin, ChevronDown, X, Check, LayoutGrid, Banknote, Search as SearchIcon, SlidersHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useProvinces, useDistricts, useWards } from '@/hooks/useAddress';
import { useBusinessSectors } from '@/hooks/useListHelper';
import { NumberInput } from '@/components/ui/numberInput';

const PRICE_RANGES = [
  { label: 'Tất cả', min: null, max: null },
  { label: 'Dưới 1 tỷ', min: 0, max: 1000000000 },
  { label: '1 - 3 tỷ', min: 1000000000, max: 3000000000 },
  { label: '3 - 5 tỷ', min: 3000000000, max: 5000000000 },
  { label: '5 - 10 tỷ', min: 5000000000, max: 10000000000 },
  { label: 'Trên 10 tỷ', min: 10000000000, max: null },
];

interface TransferFilterProps {
  onSearch: (filters: any) => void;
  initialFilters?: any;
}

export const TransferFilter: React.FC<TransferFilterProps> = ({ onSearch, initialFilters = {} }) => {
  const [search, setSearch] = useState(initialFilters.search || '');

  // Categories State
  const [selectedSectorId, setSelectedSectorId] = useState<string | null>(initialFilters.businessSectorId || null);
  const [tempSelectedSectorId, setTempSelectedSectorId] = useState<string | null>(initialFilters.businessSectorId || null);

  // Location State
  const [provinceId, setProvinceId] = useState<number | null>(initialFilters.provinceId || null);
  const [districtId, setDistrictId] = useState<number | null>(initialFilters.districtId || null);
  const [wardId, setWardId] = useState<number | null>(initialFilters.wardId || null);

  const [minPrice, setMinPrice] = useState<number | null>(initialFilters.minPrice || null);
  const [maxPrice, setMaxPrice] = useState<number | null>(initialFilters.maxPrice || null);

  // Advanced Filters
  const [minRevenue, setMinRevenue] = useState<number | null>(initialFilters.minRevenue || null);
  const [maxRevenue, setMaxRevenue] = useState<number | null>(initialFilters.maxRevenue || null);
  const [minProfit, setMinProfit] = useState<number | null>(initialFilters.minProfit || null);
  const [maxProfit, setMaxProfit] = useState<number | null>(initialFilters.maxProfit || null);
  const [minArea, setMinArea] = useState<number | null>(initialFilters.minArea || null);
  const [maxArea, setMaxArea] = useState<number | null>(initialFilters.maxArea || null);
  const [startDate, setStartDate] = useState<string | null>(initialFilters.startDate || null);
  const [endDate, setEndDate] = useState<string | null>(initialFilters.endDate || null);

  const [activeDropdown, setActiveDropdown] = useState<'sector' | 'location' | 'price' | 'advanced' | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Data Hooks
  const { data: sectors = [] } = useBusinessSectors();
  const { data: provinces = [] } = useProvinces(1); // Assuming countryId 1 is Vietnam
  const { data: districts = [] } = useDistricts(provinceId || undefined);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync temp sector state when dropdown opens
  useEffect(() => {
    if (activeDropdown === 'sector') {
      setTempSelectedSectorId(selectedSectorId);
    }
  }, [activeDropdown]);

  const applySector = () => {
    setSelectedSectorId(tempSelectedSectorId);
    onSearch({
      search: search.trim() || undefined,
      businessSectorId: tempSelectedSectorId || undefined,
      provinceId: provinceId || undefined,
      districtId: districtId || undefined,
      wardId: wardId || undefined,
      minPrice: minPrice !== null ? minPrice : undefined,
      maxPrice: maxPrice !== null ? maxPrice : undefined,
      minRevenue: minRevenue !== null ? minRevenue : undefined,
      maxRevenue: maxRevenue !== null ? maxRevenue : undefined,
      minProfit: minProfit !== null ? minProfit : undefined,
      maxProfit: maxProfit !== null ? maxProfit : undefined,
      minArea: minArea !== null ? minArea : undefined,
      maxArea: maxArea !== null ? maxArea : undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    });
    setActiveDropdown(null);
  };

  const handleSearch = () => {
    onSearch({
      search: search.trim() || undefined,
      businessSectorId: selectedSectorId || undefined,
      provinceId: provinceId || undefined,
      districtId: districtId || undefined,
      wardId: wardId || undefined,
      minPrice: minPrice !== null ? minPrice : undefined,
      maxPrice: maxPrice !== null ? maxPrice : undefined,
      minRevenue: minRevenue !== null ? minRevenue : undefined,
      maxRevenue: maxRevenue !== null ? maxRevenue : undefined,
      minProfit: minProfit !== null ? minProfit : undefined,
      maxProfit: maxProfit !== null ? maxProfit : undefined,
      minArea: minArea !== null ? minArea : undefined,
      maxArea: maxArea !== null ? maxArea : undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    });
    setActiveDropdown(null);
  };

  const resetAll = () => {
    setSearch('');
    setSelectedSectorId(null);
    setTempSelectedSectorId(null);
    setProvinceId(null);
    setDistrictId(null);
    setWardId(null);
    setMinPrice(null);
    setMaxPrice(null);
    setMinRevenue(null);
    setMaxRevenue(null);
    setMinProfit(null);
    setMaxProfit(null);
    setMinArea(null);
    setMaxArea(null);
    setStartDate(null);
    setEndDate(null);

    onSearch({
      search: undefined,
      businessSectorId: undefined,
      provinceId: undefined,
      districtId: undefined,
      wardId: undefined,
      minPrice: undefined,
      maxPrice: undefined,
      minRevenue: undefined,
      maxRevenue: undefined,
      minProfit: undefined,
      maxProfit: undefined,
      minArea: undefined,
      maxArea: undefined,
      startDate: undefined,
      endDate: undefined,
    });
    setActiveDropdown(null);
  };

  return (
    <div className="w-full max-w-7xl mx-auto" ref={containerRef}>
      <div className="relative flex flex-col md:flex-row items-stretch bg-white rounded-2xl shadow-xl border border-gray-100 p-2 gap-2 md:gap-0">

        {/* Sector Selector */}
        <div className="relative flex-1 group">
          <button
            onClick={() => setActiveDropdown(activeDropdown === 'sector' ? null : 'sector')}
            className={cn(
              "flex items-center w-full h-12 px-2 gap-1.5 rounded-xl transition-colors text-left",
              "hover:bg-gray-50",
              activeDropdown === 'sector' && "bg-gray-50"
            )}
          >
            <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center text-orange-500 shrink-0">
              <LayoutGrid size={18} />
            </div>
            <div className="flex-1 overflow-hidden">
              <p className="text-sm font-semibold text-gray-700 truncate">
                {selectedSectorId ? sectors.find((s: any) => s.id === selectedSectorId)?.name : "Tất cả mô hình"}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {selectedSectorId && (
                <div
                  onClick={(e) => { e.stopPropagation(); setSelectedSectorId(null); handleSearch(); }}
                  className="w-5 h-5 rounded-full hover:bg-gray-200 flex items-center justify-center text-gray-400 transition-colors"
                >
                  <X size={12} />
                </div>
              )}
              <ChevronDown size={16} className={cn("text-gray-400 transition-transform", activeDropdown === 'sector' && "rotate-180")} />
            </div>
          </button>

          {/* Sector Dropdown */}
          {activeDropdown === 'sector' && (
            <div className="absolute top-full left-0 mt-3 w-full md:w-[400px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden flex flex-col animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="p-4 border-b flex items-center justify-between bg-gray-50/50">
                <h3 className="font-bold text-gray-800">Mô hình kinh doanh</h3>
                <button onClick={() => setTempSelectedSectorId(null)} className="text-xs text-orange-500 font-medium hover:underline">Xóa chọn</button>
              </div>
              <div className="max-h-[300px] overflow-y-auto custom-scrollbar p-2">
                <div className="grid grid-cols-1 gap-1">
                  <button
                    onClick={() => setTempSelectedSectorId(null)}
                    className={cn(
                      "flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-all",
                      !tempSelectedSectorId ? "bg-orange-50 text-orange-600 font-semibold" : "text-gray-600 hover:bg-gray-50"
                    )}
                  >
                    <span>Tất cả mô hình</span>
                    {!tempSelectedSectorId && <Check size={16} />}
                  </button>
                  {sectors.map((s: any) => (
                    <button
                      key={s.id}
                      onClick={() => setTempSelectedSectorId(s.id)}
                      className={cn(
                        "flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-all",
                        tempSelectedSectorId === s.id ? "bg-orange-50 text-orange-600 font-semibold" : "text-gray-600 hover:bg-gray-50"
                      )}
                    >
                      <span>{s.name}</span>
                      {tempSelectedSectorId === s.id && <Check size={16} />}
                    </button>
                  ))}
                </div>
              </div>
              <div className="p-4 bg-gray-50 border-t flex items-center justify-end gap-2">
                <button onClick={() => setActiveDropdown(null)} className="px-6 h-10 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-white transition-colors">Hủy</button>
                <button onClick={applySector} className="px-8 h-10 rounded-xl bg-orange-500 text-sm font-bold text-white hover:bg-orange-600 transition-all shadow-md shadow-orange-200">Áp dụng</button>
              </div>
            </div>
          )}
        </div>

        <div className="hidden md:block w-px h-8 self-center bg-gray-100 mx-1" />

        {/* Search Input */}
        <div className="flex-[1.2] flex items-center px-3 group min-w-0">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-500 shrink-0 mr-2">
            <SearchIcon size={18} />
          </div>
          <div className="flex-1 relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Nhập từ khóa tìm kiếm..."
              className="w-full text-sm font-semibold text-gray-700 bg-transparent outline-none placeholder:text-gray-300 pr-8"
            />
            {search && (
              <button
                onClick={() => { setSearch(''); onSearch({ ...initialFilters, search: undefined }); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 transition-colors"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>

        <div className="hidden md:block w-px h-8 self-center bg-gray-100 mx-1" />

        {/* Location Selector */}
        <div className="relative flex-1 group">
          <button
            onClick={() => setActiveDropdown(activeDropdown === 'location' ? null : 'location')}
            className={cn(
              "flex items-center w-full h-12 px-2 gap-1.5 rounded-xl transition-colors text-left",
              "hover:bg-gray-50",
              activeDropdown === 'location' && "bg-gray-50"
            )}
          >
            <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center text-green-500 shrink-0">
              <MapPin size={18} />
            </div>
            <div className="flex-1 overflow-hidden">
              <p className="text-sm font-semibold text-gray-700 truncate">
                {provinceId ? provinces.find((p: any) => p.id === provinceId)?.name : "Toàn quốc"}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {provinceId !== null && (
                <div
                  onClick={(e) => { e.stopPropagation(); setProvinceId(null); setDistrictId(null); handleSearch(); }}
                  className="w-5 h-5 rounded-full hover:bg-gray-200 flex items-center justify-center text-gray-400 transition-colors"
                >
                  <X size={12} />
                </div>
              )}
              <ChevronDown size={16} className={cn("text-gray-400 transition-transform", activeDropdown === 'location' && "rotate-180")} />
            </div>
          </button>

          {/* Location Dropdown */}
          {activeDropdown === 'location' && (
            <div className="absolute top-full right-0 mt-3 w-full md:w-[700px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden flex flex-col animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="p-4 border-b flex items-center justify-between bg-gray-50/50">
                <span className="font-bold text-gray-800">Khu vực</span>
                <button onClick={() => { setProvinceId(null); setDistrictId(null); }} className="text-xs text-orange-500 font-medium hover:underline">Xóa chọn</button>
              </div>

              <div className="flex h-[300px]">
                {/* Left: Provinces */}
                <div className="w-2/5 border-r overflow-y-auto custom-scrollbar">
                  <div className="p-2 space-y-1">
                    {provinces.map((p: any) => (
                      <div
                        key={p.id}
                        onClick={() => setProvinceId(p.id)}
                        className={cn(
                          "flex items-center justify-between px-3 py-2.5 rounded-lg text-sm cursor-pointer transition-all",
                          provinceId === p.id ? "bg-orange-50 text-orange-600 font-semibold" : "text-gray-600 hover:bg-gray-50"
                        )}
                      >
                        <span className="truncate">{p.name}</span>
                        <ChevronDown size={14} className="-rotate-90 text-gray-300" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right: Districts */}
                <div className="w-3/5 overflow-y-auto custom-scrollbar p-4">
                  {provinceId ? (
                    <div className="grid grid-cols-1 gap-1">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">QUẬN / HUYỆN</p>
                      {districts.map((d: any) => (
                        <label
                          key={d.id}
                          className={cn(
                            "flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer",
                            districtId === d.id ? "bg-orange-50 text-orange-600 font-semibold" : "text-gray-600 hover:bg-gray-50"
                          )}
                        >
                          <span>{d.name}</span>
                          <div className={cn(
                            "w-4 h-4 rounded border flex items-center justify-center",
                            districtId === d.id ? "bg-orange-500 border-orange-500" : "border-gray-300"
                          )}>
                            {districtId === d.id && <Check size={12} className="text-white" />}
                          </div>
                          <input
                            type="radio"
                            name="district"
                            className="hidden"
                            checked={districtId === d.id}
                            onChange={() => setDistrictId(d.id)}
                          />
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400 space-y-3">
                      <MapPin size={40} className="opacity-20" />
                      <p className="text-sm">Vui lòng chọn Tỉnh/Thành phố</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4 bg-gray-50 border-t flex items-center justify-end gap-2">
                <button onClick={() => setActiveDropdown(null)} className="px-6 h-10 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-white transition-colors">Hủy</button>
                <button onClick={handleSearch} className="px-8 h-10 rounded-xl bg-orange-500 text-sm font-bold text-white hover:bg-orange-600 transition-all shadow-md shadow-orange-200">Áp dụng</button>
              </div>
            </div>
          )}
        </div>

        <div className="hidden md:block w-px h-8 self-center bg-gray-100 mx-1" />

        {/* Price Selector */}
        <div className="relative flex-1 group">
          <button
            onClick={() => setActiveDropdown(activeDropdown === 'price' ? null : 'price')}
            className={cn(
              "flex items-center w-full h-12 px-2 gap-1.5 rounded-xl transition-colors text-left",
              "hover:bg-gray-50",
              activeDropdown === 'price' && "bg-gray-50"
            )}
          >
            <div className="w-8 h-8 rounded-lg bg-yellow-50 flex items-center justify-center text-yellow-600 shrink-0">
              <Banknote size={18} />
            </div>
            <div className="flex-1 overflow-hidden">
              <p className="text-sm font-semibold text-gray-700 truncate">
                {minPrice === null && maxPrice === null ? "Mức giá" :
                  minPrice !== null && maxPrice !== null ? `${minPrice / 1000000000} - ${maxPrice / 1000000000} tỷ` :
                    minPrice !== null ? `Trên ${minPrice / 1000000000} tỷ` :
                      maxPrice !== null ? `Dưới ${maxPrice / 1000000000} tỷ` : "Mức giá"}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {(minPrice !== null || maxPrice !== null) && (
                <div
                  onClick={(e) => { e.stopPropagation(); setMinPrice(null); setMaxPrice(null); handleSearch(); }}
                  className="w-5 h-5 rounded-full hover:bg-gray-200 flex items-center justify-center text-gray-400 transition-colors"
                >
                  <X size={12} />
                </div>
              )}
              <ChevronDown size={16} className={cn("text-gray-400 transition-transform", activeDropdown === 'price' && "rotate-180")} />
            </div>
          </button>

          {/* Price Dropdown */}
          {activeDropdown === 'price' && (
            <div className="absolute top-full left-0 mt-3 w-full md:w-[450px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden flex flex-col animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="p-4 border-b flex items-center justify-between bg-gray-50/50">
                <h3 className="font-bold text-gray-800">Chọn khoảng giá</h3>
                <button onClick={() => { setMinPrice(null); setMaxPrice(null); }} className="text-xs text-orange-500 font-medium hover:underline">Xóa chọn</button>
              </div>

              <div className="max-h-[300px] overflow-y-auto custom-scrollbar p-5">
                <div className="grid grid-cols-2 gap-2 mb-6">
                  {PRICE_RANGES.map((range, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setMinPrice(range.min);
                        setMaxPrice(range.max);
                      }}
                      className={cn(
                        "flex items-center gap-2 p-2.5 rounded-xl border text-left transition-all",
                        (minPrice === range.min && maxPrice === range.max)
                          ? "border-orange-500 bg-orange-50/50 text-orange-600 font-semibold"
                          : "border-gray-100 hover:border-orange-200 hover:bg-gray-50 text-gray-600"
                      )}
                    >
                      <div className={cn(
                        "w-4 h-4 rounded-full border flex items-center justify-center transition-all",
                        (minPrice === range.min && maxPrice === range.max) ? "border-orange-500 border-4" : "border-gray-300"
                      )} />
                      <span className="text-sm">{range.label}</span>
                    </button>
                  ))}
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Hoặc nhập khoảng giá (tỷ VNĐ)</p>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 relative">
                      <input
                        type="number"
                        min={0}
                        placeholder="Từ"
                        value={minPrice ? minPrice / 1000000000 : ''}
                        onChange={(e) => setMinPrice(e.target.value ? Math.max(0, Number(e.target.value)) * 1000000000 : null)}
                        className="w-full h-10 px-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none text-sm"
                      />
                    </div>
                    <span className="text-gray-400">-</span>
                    <div className="flex-1 relative">
                      <input
                        type="number"
                        min={0}
                        placeholder="Đến"
                        value={maxPrice ? maxPrice / 1000000000 : ''}
                        onChange={(e) => setMaxPrice(e.target.value ? Math.max(0, Number(e.target.value)) * 1000000000 : null)}
                        className="w-full h-10 px-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gray-50 border-t flex items-center justify-end gap-2">
                <button onClick={() => setActiveDropdown(null)} className="px-6 h-10 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-white transition-colors">Hủy</button>
                <button onClick={handleSearch} className="px-8 h-10 rounded-xl bg-orange-500 text-sm font-bold text-white hover:bg-orange-600 transition-all shadow-md shadow-orange-200">Áp dụng</button>
              </div>
            </div>
          )}
        </div>

        <div className="hidden md:block w-px h-8 self-center bg-gray-100 mx-1" />

        {/* Advanced Filters */}
        <div className="relative flex-[0.5] group">
          <button
            onClick={() => setActiveDropdown(activeDropdown === 'advanced' ? null : 'advanced')}
            className={cn(
              "flex items-center justify-center w-full h-12 px-2 gap-1.5 rounded-xl transition-colors",
              "hover:bg-gray-50",
              activeDropdown === 'advanced' && "bg-gray-50"
            )}
            title="Lọc nâng cao"
          >
            <SlidersHorizontal size={18} className="text-gray-500" />
            <ChevronDown size={14} className={cn("text-gray-400 transition-transform hidden lg:block", activeDropdown === 'advanced' && "rotate-180")} />
          </button>

          {/* Advanced Dropdown */}
          {activeDropdown === 'advanced' && (
            <div className="absolute top-full right-0 mt-3 w-full md:w-[600px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden flex flex-col animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="p-4 border-b flex items-center justify-between bg-gray-50/50">
                <h3 className="font-bold text-gray-800">Lọc nâng cao</h3>
                <button onClick={() => {
                  setMinRevenue(null);
                  setMaxRevenue(null);
                  setMinProfit(null);
                  setMaxProfit(null);
                  setMinArea(null);
                  setMaxArea(null);
                  setStartDate(null);
                  setEndDate(null);
                }} className="text-xs text-orange-500 font-medium hover:underline">Xóa chọn</button>
              </div>

              <div className="p-5 grid grid-cols-2 gap-4 max-h-[400px] overflow-y-auto">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Doanh thu tối thiểu (VNĐ)</label>
                  <NumberInput
                    placeholder="VD: 50,000,000"
                    value={minRevenue || ''}
                    onChange={(e) => setMinRevenue(e.target.value ? Number(e.target.value) : null)}
                    className="w-full h-10 px-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 focus:shadow-md outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Doanh thu tối đa (VNĐ)</label>
                  <NumberInput
                    placeholder="VD: 150,000,000"
                    value={maxRevenue || ''}
                    onChange={(e) => setMaxRevenue(e.target.value ? Number(e.target.value) : null)}
                    className="w-full h-10 px-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 focus:shadow-md outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Lợi nhuận tối thiểu (VNĐ)</label>
                  <NumberInput
                    placeholder="VD: 10,000,000"
                    value={minProfit || ''}
                    onChange={(e) => setMinProfit(e.target.value ? Number(e.target.value) : null)}
                    className="w-full h-10 px-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 focus:shadow-md outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Lợi nhuận tối đa (VNĐ)</label>
                  <NumberInput
                    placeholder="VD: 50,000,000"
                    value={maxProfit || ''}
                    onChange={(e) => setMaxProfit(e.target.value ? Number(e.target.value) : null)}
                    className="w-full h-10 px-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 focus:shadow-md outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Diện tích từ (m²)</label>
                  <NumberInput
                    placeholder="VD: 30"
                    value={minArea || ''}
                    onChange={(e) => setMinArea(e.target.value ? Number(e.target.value) : null)}
                    className="w-full h-10 px-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 focus:shadow-md outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Đến (m²)</label>
                  <NumberInput
                    placeholder="VD: 100"
                    value={maxArea || ''}
                    onChange={(e) => setMaxArea(e.target.value ? Number(e.target.value) : null)}
                    className="w-full h-10 px-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 focus:shadow-md outline-none text-sm"
                  />
                </div>
                {/* <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Từ ngày</label>
                  <input
                    type="date"
                    value={startDate || ''}
                    onChange={(e) => setStartDate(e.target.value || null)}
                    className="w-full h-10 px-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Đến ngày</label>
                  <input
                    type="date"
                    value={endDate || ''}
                    onChange={(e) => setEndDate(e.target.value || null)}
                    className="w-full h-10 px-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none text-sm"
                  />
                </div> */}
              </div>

              <div className="p-4 bg-gray-50 border-t flex items-center justify-end gap-2">
                <button onClick={() => setActiveDropdown(null)} className="px-6 h-10 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-white transition-colors">Hủy</button>
                <button onClick={handleSearch} className="px-8 h-10 rounded-xl bg-orange-500 text-sm font-bold text-white hover:bg-orange-600 transition-all shadow-md shadow-orange-200">Áp dụng</button>
              </div>
            </div>
          )}
        </div>

        {/* Search Button */}
        <div className="p-1 flex gap-2">
          <button
            onClick={resetAll}
            className={cn(
              "hidden lg:flex items-center justify-center gap-1 h-10 md:h-full px-4 rounded-xl border",
              "text-gray-500 text-sm font-medium hover:bg-gray-50 transition-colors"
            )}
            title="Xóa lọc"
          >
            <X size={16} />
          </button>
          <button
            onClick={handleSearch}
            className={cn(
              "flex-1 md:flex-none flex items-center justify-center gap-2 h-10 md:h-full px-8 rounded-xl",
              "bg-orange-500 text-white font-bold text-sm shadow-lg shadow-orange-100",
              "hover:bg-orange-600 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
            )}
          >
            <SearchIcon size={18} />
            <span className="md:hidden lg:inline">Tìm kiếm</span>
          </button>
        </div>
      </div>
    </div>
  );
};
